<template>
  <div class="vault-registry-editor">
    <div class="vault-registry-editor__header">
      <div>
        <h4>Vault Registry</h4>
        <p>Manage named vault endpoints used by secret references.</p>
      </div>
    </div>

    <div v-if="entries.length === 0" class="vault-registry-editor__empty">
      <span>No vault entries configured.</span>
    </div>

    <div v-else class="vault-registry-editor__entries">
      <div
        v-for="([alias, entry]) in entries"
        :key="alias"
        class="vault-entry"
      >
        <div class="vault-entry__row vault-entry__row--header">
          <div class="vault-entry__alias">{{ alias }}</div>
          <Button
            v-if="!readonly"
            variant="secondary"
            size="small"
            @click="removeEntry(alias)"
          >
            Remove
          </Button>
        </div>

        <div class="vault-entry__row">
          <div class="vault-entry__field">
            <label>Provider</label>
            <SelectBox
              :model-value="entry.provider"
              :options="providerOptions"
              :disabled="readonly"
              @update:model-value="updateEntry(alias, 'provider', $event.toString())"
            />
          </div>

          <div class="vault-entry__field vault-entry__field--wide">
            <label>Vault URI</label>
            <InputBox
              :model-value="entry.uri"
              type="url"
              placeholder="https://example.vault.azure.net/"
              :readonly="readonly"
              :has-error="hasUriError(entry.uri)"
              :error-messages="getUriErrors(entry.uri)"
              @update:model-value="updateEntry(alias, 'uri', $event.toString())"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!readonly" class="vault-registry-editor__add">
      <h5>Add Vault Entry</h5>
      <div class="vault-entry__row">
        <div class="vault-entry__field">
          <label>Alias</label>
          <InputBox
            :model-value="newAlias"
            placeholder="default"
            :has-error="newAliasErrors.length > 0"
            :error-messages="newAliasErrors"
            @update:model-value="newAlias = $event.toString()"
          />
        </div>

        <div class="vault-entry__field">
          <label>Provider</label>
          <SelectBox
            :model-value="newProvider"
            :options="providerOptions"
            @update:model-value="newProvider = $event.toString()"
          />
        </div>

        <div class="vault-entry__field vault-entry__field--wide">
          <label>Vault URI</label>
          <InputBox
            :model-value="newUri"
            type="url"
            placeholder="https://example.vault.azure.net/"
            :has-error="newUriErrors.length > 0"
            :error-messages="newUriErrors"
            @update:model-value="newUri = $event.toString()"
          />
        </div>
      </div>

      <div class="vault-registry-editor__actions">
        <Button
          variant="secondary"
          size="small"
          :disabled="!canAddEntry"
          @click="addEntry"
        >
          Add Vault
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from '@/components/atoms/Button.vue'
import InputBox from '@/components/atoms/InputBox.vue'
import SelectBox from '@/components/atoms/SelectBox.vue'
import type { VaultEntry } from '@/types/configuration'

interface Props {
  modelValue: Record<string, VaultEntry>
  readonly?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: Record<string, VaultEntry>): void
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
})

const emit = defineEmits<Emits>()

const newAlias = ref('')
const newProvider = ref('azure-keyvault')
const newUri = ref('')

const providerOptions = [
  { label: 'Azure Key Vault', value: 'azure-keyvault' },
]

const entries = computed(() => Object.entries(props.modelValue || {}).sort(([left], [right]) => left.localeCompare(right)))

const newAliasErrors = computed(() => {
  const alias = newAlias.value.trim()
  if (!alias) {
    return []
  }

  const errors: string[] = []
  if (!/^[A-Za-z][A-Za-z0-9-]*$/.test(alias)) {
    errors.push('Use letters, numbers, and hyphens only.')
  }

  if (props.modelValue[alias]) {
    errors.push('Alias already exists.')
  }

  return errors
})

const newUriErrors = computed(() => getUriErrors(newUri.value))

const canAddEntry = computed(() => {
  return newAlias.value.trim().length > 0
    && newAliasErrors.value.length === 0
    && newUriErrors.value.length === 0
    && newUri.value.trim().length > 0
})

function getUriErrors(uri: string): string[] {
  const trimmed = uri.trim()
  if (!trimmed) {
    return []
  }

  try {
    const parsed = new URL(trimmed)
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return ['Vault URI must start with http:// or https://.']
    }

    return []
  } catch {
    return ['Enter a valid absolute vault URI.']
  }
}

function hasUriError(uri: string): boolean {
  return getUriErrors(uri).length > 0
}

function emitValue(value: Record<string, VaultEntry>) {
  emit('update:modelValue', value)
}

function updateEntry(alias: string, field: keyof VaultEntry, value: string) {
  const next = structuredClone(props.modelValue || {})
  next[alias] = {
    ...next[alias],
    [field]: value,
  }
  emitValue(next)
}

function removeEntry(alias: string) {
  const next = structuredClone(props.modelValue || {})
  delete next[alias]
  emitValue(next)
}

function addEntry() {
  if (!canAddEntry.value) {
    return
  }

  const alias = newAlias.value.trim()
  const next = structuredClone(props.modelValue || {})
  next[alias] = {
    provider: newProvider.value,
    uri: newUri.value.trim(),
  }

  emitValue(next)
  newAlias.value = ''
  newProvider.value = 'azure-keyvault'
  newUri.value = ''
}
</script>

<style scoped lang="scss">
.vault-registry-editor {
  display: grid;
  gap: 16px;
  padding: 12px;
  border: 1px solid var(--vscode-widget-border);
  border-radius: 8px;
  background: var(--vscode-editorWidget-background, rgba(127, 127, 127, 0.06));
}

.vault-registry-editor__header {
  h4,
  h5 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--vscode-descriptionForeground);
  }
}

.vault-registry-editor__empty {
  padding: 12px;
  border: 1px dashed var(--vscode-widget-border);
  border-radius: 6px;
  color: var(--vscode-descriptionForeground);
  font-size: 12px;
}

.vault-registry-editor__entries,
.vault-registry-editor__add {
  display: grid;
  gap: 12px;
}

.vault-entry {
  display: grid;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--vscode-widget-border);
  border-radius: 6px;
  background: var(--vscode-input-background);
}

.vault-entry__row {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(160px, 220px) minmax(180px, 220px) minmax(260px, 1fr);
}

.vault-entry__row--header {
  grid-template-columns: 1fr auto;
  align-items: center;
}

.vault-entry__alias {
  font-size: 12px;
  font-weight: 600;
  color: var(--vscode-charts-blue, #4fc3f7);
}

.vault-entry__field {
  display: grid;
  gap: 6px;

  label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--vscode-descriptionForeground);
  }
}

.vault-entry__field--wide {
  min-width: 0;
}

.vault-registry-editor__actions {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 900px) {
  .vault-entry__row {
    grid-template-columns: 1fr;
  }
}
</style>